import { api } from '../../api'
import type {
  DashboardQueryParams,
  DashboardStatsResponse,
  DashboardInvalidateResponse,
  DashboardV3Response,
  SellerInfringementStatsPoint,
} from '../../../types/dashboard.types'

const buildDashboardRequestParams = (data: DashboardQueryParams) => {
  const unitsJoined = data.parameters.units?.join(',')
  return {
    ...data.filters,
    otp_token: data.parameters.otp_token ?? undefined,
    table: data.type,
    metrics: data.parameters.metric.join(','),
    graph_unit: data.parameters.graph_unit || undefined,
    unit: data.parameters.unit || undefined,
    units: unitsJoined || undefined,
    group_by: data.parameters.category.join(','),
    limit: data.parameters.limit,
    exclude_nulls: data.parameters.exclude_nulls,
    reverse: data.parameters.reverse,
  }
}

export const getDashboardV3 = async (data: DashboardQueryParams): Promise<DashboardV3Response> => {
  const params = buildDashboardRequestParams(data)
  const res = await api.get('dashboard/v3', { params })
  return res.data
}

export const getDashboardStats = async (data: DashboardQueryParams): Promise<DashboardStatsResponse> => {
  const params = buildDashboardRequestParams(data)
  const res = await api.get('dashboard/stats', { params })
  return {
    data: res.data?.data ?? res.data,
    timestamp: res.data?.timestamp as string | undefined,
  }
}

export const getDashboardInvalidate = async (data: DashboardQueryParams): Promise<DashboardInvalidateResponse> => {
  const params = buildDashboardRequestParams(data)
  const res = await api.get('dashboard/stats/invalidate', { params })
  return res.data?.data ?? res.data
}

const toNumber = (value: unknown): number => {
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value === 'string') {
    const parsed = parseFloat(value)
    return Number.isFinite(parsed) ? parsed : 0
  }
  return 0
}

const normalizeSellerInfringementData = (data: unknown[]): SellerInfringementStatsPoint[] => {
  return data
    .map((row) => {
      if (!row || typeof row !== 'object') return null
      const record = row as Record<string, unknown>
      const rawTimestamp =
        (record.timestamp as string | undefined) ??
        (record.date as string | undefined) ??
        (record.day as string | undefined) ??
        (record.period as string | undefined) ??
        (record.name as string | undefined)

      if (!rawTimestamp) return null

      const timestamp =
        typeof rawTimestamp === 'string' ? rawTimestamp.split(/[ T]/)[0] ?? rawTimestamp : String(rawTimestamp)

      return {
        timestamp,
        flagged: toNumber(record.flagged ?? record.Flagged ?? record.total_flagged),
        ntds_sent: toNumber(record.ntds_sent ?? record['NTDs Sent'] ?? record.ntds),
        removed: toNumber(record.removed ?? record.Removed ?? record.total_removed),
        compliance_rate: toNumber(
          record.compliance_rate ?? record['Compliance Rate'] ?? record.complianceRate ?? record.compliance
        ),
      }
    })
    .filter((point): point is SellerInfringementStatsPoint => point !== null)
    .sort((a, b) => {
      const aTime = Date.parse(a.timestamp)
      const bTime = Date.parse(b.timestamp)
      if (Number.isNaN(aTime) || Number.isNaN(bTime)) return 0
      return aTime - bTime
    })
}

export const getSellerInfringementStats = async (
  sellerId: string,
  fromDate: string,
  toDate: string,
  graphUnit: 'day' | 'week' | 'month' = 'day'
): Promise<SellerInfringementStatsPoint[]> => {
  const response = await api.get('dashboard/v3', {
    params: {
      table: 'items',
      metrics: 'flagged,ntds_sent,removed,compliance_rate',
      seller_id: sellerId,
      from_date: fromDate,
      to_date: toDate,
      unit: 'count',
      graph_unit: graphUnit,
      order: 'asc',
    },
  })

  const payload = response.data
  const candidateArrays: unknown[] = [payload, payload?.graph, payload?.data, payload?.results, payload?.items].filter(
    Array.isArray
  )
  const series = candidateArrays.length > 0 ? (candidateArrays[0] as unknown[]) : Array.isArray(payload) ? payload : []
  return normalizeSellerInfringementData(series)
}

export const getPlatformInfringementStats = async (
  platformId: string,
  fromDate: string,
  toDate: string,
  graphUnit: 'day' | 'week' | 'month' = 'day'
): Promise<SellerInfringementStatsPoint[]> => {
  const response = await api.get('dashboard/v3', {
    params: {
      table: 'items',
      metrics: 'flagged,ntds_sent,removed,compliance_rate',
      platform_id: platformId,
      from_date: fromDate,
      to_date: toDate,
      unit: 'count',
      graph_unit: graphUnit,
      order: 'asc',
    },
  })

  const payload = response.data
  const candidateArrays: unknown[] = [payload, payload?.graph, payload?.data, payload?.results, payload?.items].filter(
    Array.isArray
  )
  const series = candidateArrays.length > 0 ? (candidateArrays[0] as unknown[]) : Array.isArray(payload) ? payload : []
  return normalizeSellerInfringementData(series)
}
